import { ArrowRight } from "lucide-react"
import { useEffect } from "react"
import { Link, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { captureAnalyticsEvent, getAnalyticsIdentity } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"
import type { MarketingPageProps } from "@/types/marketing"

type WebsiteReviewData = {
  company_name: string
  contact_name: string
  email: string
  fax_number: string
  message: string
  posthog_distinct_id: string
  privacy_acknowledged: boolean
  submission: string
  website_url: string
}

type Props = PageProps<MarketingPageProps>

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null

  return <p className="website-review-error type-marketing-caption" id={id} role="alert">{message}</p>
}

export default function MarketingWebsiteReview(props: Props) {
  const config = props.website_review_form!
  const form = useForm<WebsiteReviewData>({
    company_name: "",
    contact_name: "",
    email: "",
    fax_number: "",
    message: "",
    posthog_distinct_id: "",
    privacy_acknowledged: false,
    submission: "",
    website_url: "",
  })
  const errors = { ...props.errors, ...form.errors }
  const copy = props.locale === "hr" ? {
    contactName: "Ime i prezime",
    email: "Poslovna e-adresa",
    company: "Naziv tvrtke",
    website: "Adresa web-stranice",
    context: "Što vam danas djeluje zastarjelo ili teško?",
    optional: "Opcionalno",
    consent: "Pročitao/la sam Obavijest o privatnosti i želim da Zmile obradi ove podatke kako bi odgovorio na moj zahtjev.",
    submit: "Pošaljite na provjeru",
    submitting: "Slanje…",
    privacy: "Obavijest o privatnosti",
    response: "Osobni odgovor u roku od tri radna dana",
    responseBody: "Dobit ćete nekoliko konkretnih zapažanja i prijedlog korisnog sljedećeg koraka — ne generički automatski izvještaj.",
    scope: "Što provjera nije",
    scopeBody: "Slanje ne jamči besplatan mockup, redizajn ni ponudu. Cilj je dati pošten, koristan prvi pogled.",
  } : {
    contactName: "Contact name",
    email: "Work email",
    company: "Company name",
    website: "Website URL",
    context: "What feels outdated or difficult today?",
    optional: "Optional",
    consent: "I have read the Privacy notice and want Zmile to process this information to respond to my request.",
    submit: "Submit for review",
    submitting: "Submitting…",
    privacy: "Privacy notice",
    response: "A personal response within three business days",
    responseBody: "You’ll receive a few specific observations and a useful suggested next step — not a generic automated report.",
    scope: "What the review is not",
    scopeBody: "Submitting does not guarantee a free mockup, redesign, or proposal. The purpose is an honest, useful first look.",
  }

  useEffect(() => {
    captureAnalyticsEvent("website_review_form_viewed", {
      locale: props.locale,
      page_id: props.page_key,
      lead_kind: "website_review",
    })
    const distinctId = getAnalyticsIdentity()
    if (distinctId) form.setData("posthog_distinct_id", distinctId)
  }, [])

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const distinctId = getAnalyticsIdentity() || form.data.posthog_distinct_id
    form.transform((data) => ({
      authenticity_token: config.authenticity_token,
      form_started_at: config.started_at,
      fax_number: data.fax_number,
      lead: {
        company_name: data.company_name,
        contact_name: data.contact_name,
        email: data.email,
        message: data.message,
        posthog_distinct_id: distinctId,
        privacy_acknowledged: data.privacy_acknowledged,
        website_url: data.website_url,
      },
    }))
    form.post(config.action, {
      preserveScroll: true,
      onSuccess: () => captureAnalyticsEvent("website_review_submitted", {
        locale: props.locale,
        page_id: props.page_key,
        lead_kind: "website_review",
      }),
    })
  }

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        title={props.title}
      />
      <MarketingLayout
        consultationHref={props.consultation_href}
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
      >
        <section className="website-review-page">
          <header className="website-review-intro">
            <h1 className="type-marketing-hero-display">{props.heading}</h1>
            <p className="type-marketing-lead">{props.summary}</p>
          </header>

          <div className="website-review-grid">
            <form action={config.action} className="website-review-form" method="post" noValidate onSubmit={submit}>
              <input name="authenticity_token" type="hidden" value={config.authenticity_token} />
              <input name="form_started_at" type="hidden" value={config.started_at} />
              <input name="lead[posthog_distinct_id]" type="hidden" value={form.data.posthog_distinct_id} />
              <div aria-hidden="true" className="website-review-honeypot">
                <label htmlFor="fax_number">Fax</label>
                <Input
                  autoComplete="off"
                  id="fax_number"
                  name="fax_number"
                  onChange={(event) => form.setData("fax_number", event.target.value)}
                  tabIndex={-1}
                  value={form.data.fax_number}
                />
              </div>

              {errors.submission && (
                <p className="website-review-submission-error type-marketing-body-small" role="alert">
                  {errors.submission}
                </p>
              )}

              <div className="website-review-fields-two">
                <div className="website-review-field">
                  <label htmlFor="contact_name">{copy.contactName}</label>
                  <Input
                    aria-describedby={errors.contact_name ? "contact_name_error" : undefined}
                    aria-invalid={Boolean(errors.contact_name)}
                    autoComplete="name"
                    id="contact_name"
                    name="lead[contact_name]"
                    onChange={(event) => form.setData("contact_name", event.target.value)}
                    required
                    value={form.data.contact_name}
                  />
                  <FieldError id="contact_name_error" message={errors.contact_name} />
                </div>
                <div className="website-review-field">
                  <label htmlFor="email">{copy.email}</label>
                  <Input
                    aria-describedby={errors.email ? "email_error" : undefined}
                    aria-invalid={Boolean(errors.email)}
                    autoComplete="email"
                    id="email"
                    name="lead[email]"
                    onChange={(event) => form.setData("email", event.target.value)}
                    required
                    type="email"
                    value={form.data.email}
                  />
                  <FieldError id="email_error" message={errors.email} />
                </div>
              </div>

              <div className="website-review-field">
                <label htmlFor="company_name">{copy.company}</label>
                <Input
                  aria-describedby={errors.company_name ? "company_name_error" : undefined}
                  aria-invalid={Boolean(errors.company_name)}
                  autoComplete="organization"
                  id="company_name"
                  name="lead[company_name]"
                  onChange={(event) => form.setData("company_name", event.target.value)}
                  required
                  value={form.data.company_name}
                />
                <FieldError id="company_name_error" message={errors.company_name} />
              </div>

              <div className="website-review-field">
                <label htmlFor="website_url">{copy.website}</label>
                <Input
                  aria-describedby={errors.website_url ? "website_url_error" : undefined}
                  aria-invalid={Boolean(errors.website_url)}
                  autoComplete="url"
                  id="website_url"
                  inputMode="url"
                  name="lead[website_url]"
                  onChange={(event) => form.setData("website_url", event.target.value)}
                  placeholder="https://example.com"
                  required
                  value={form.data.website_url}
                />
                <FieldError id="website_url_error" message={errors.website_url} />
              </div>

              <div className="website-review-field">
                <label htmlFor="message">{copy.context} <span className="type-marketing-caption">({copy.optional})</span></label>
                <textarea
                  aria-describedby={errors.message ? "message_error" : undefined}
                  aria-invalid={Boolean(errors.message)}
                  className="form-control form-control-textarea"
                  id="message"
                  maxLength={5000}
                  name="lead[message]"
                  onChange={(event) => form.setData("message", event.target.value)}
                  value={form.data.message}
                />
                <FieldError id="message_error" message={errors.message} />
              </div>

              <div className="website-review-consent">
                <Checkbox
                  aria-describedby={errors.privacy_acknowledged ? "privacy_acknowledged_error" : undefined}
                  aria-invalid={Boolean(errors.privacy_acknowledged)}
                  checked={form.data.privacy_acknowledged}
                  className="website-review-consent-control"
                  id="privacy_acknowledged"
                  name="lead[privacy_acknowledged]"
                  onChange={(event) => form.setData("privacy_acknowledged", event.target.checked)}
                  required
                  value="1"
                />
                <div>
                  <label className="font-normal text-ink-body" htmlFor="privacy_acknowledged">
                    {copy.consent} <Link className="marketing-consent-link" href={props.footer.privacy_href}>{copy.privacy}</Link>.
                  </label>
                  <FieldError id="privacy_acknowledged_error" message={errors.privacy_acknowledged} />
                </div>
              </div>

              <Button disabled={form.processing} size="marketingLg" type="submit" variant="marketing">
                {form.processing ? copy.submitting : copy.submit}
                <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
              </Button>
            </form>

            <aside className="website-review-expectations" aria-label={props.locale === "hr" ? "Što očekivati" : "What to expect"}>
              <div>
                <span className="type-marketing-index">01</span>
                <h2 className="type-marketing-list-display">{copy.response}</h2>
                <p className="type-marketing-body-small">{copy.responseBody}</p>
              </div>
              <div>
                <span className="type-marketing-index">02</span>
                <h2 className="type-marketing-list-display">{copy.scope}</h2>
                <p className="type-marketing-body-small">{copy.scopeBody}</p>
              </div>
            </aside>
          </div>
        </section>
      </MarketingLayout>
    </>
  )
}
